import { Box3, Material, Mesh, Object3D, TangentSpaceNormalMap, Vector2, Vector3 } from 'three';
import { GlobalAttributeData } from '../configurator/product/product-configuration/product-configuration.component';
import {
  EDGE_DIRECTION_STRING,
  EdgeDirectionEnum,
  IS_EDGE_ATTRIBUTE_STRING,
} from '../configurator/product/product-configuration/services/edge-controller.service';
import { StepDTO } from '../step-modules/step/models/step';
import {
  colisionDescriptorsToBoundingBox,
  colisionDescriptorsToCenteredBoundingBox,
  getAttributeValuePairType,
} from '../utils/Utils';
import { AttributeTypeENUM, ProductVisibilityENUM } from './enums/ProductEnums';
import { CollisionDescriptorUsageType } from './interfaces/GenericInterfaces';
import { AttributeDTO } from './product-models/DTOs/AttributeDTO';
import { AttributeSettingsDTO } from './product-models/DTOs/AttributeSettingsDTO';
import { AttributeValueDTO } from './product-models/DTOs/AttributeValueDTO';
import { ProductBlueprintDTO } from './product-models/DTOs/ProductBlueprintDTO';
import { ProductDTO } from './product-models/DTOs/ProductDTO';
import { Attribute } from './product-models/views/Attribute';
import { AttributeValue, ModelValue } from './product-models/views/AttributeValue';
import {
  AttributeMap,
  AttributePair,
  ATTRIBUTES_THAT_ARENT_IMMIDIATELY_APPLIED,
  AttributeSetting,
  Product,
} from './product-models/views/Product';
import { ProductBlueprint } from './product-models/views/ProductBlueprint';
import { ProductRelationship } from './product-models/views/ProductRelationship';
import { Wall } from './product-models/views/Wall';

export const BLENDA_STRING = 'isBlenda';

export function removeObjectsWithChildren(obj: Object3D) {
  if (obj.children.length > 0) {
    for (let x = obj.children.length - 1; x >= 0; x--) {
      removeObjectsWithChildren(obj.children[x]);
    }
  }

  if (obj instanceof Mesh) {
    disposeMesh(obj);
  }

  return false;
}

export function getAllMaterialNamesFromModel(model: Object3D) {
  const names: string[] = [];
  model.traverse((child) => {
    if (child instanceof Mesh) {
      if (child.material instanceof Material) {
        names.push(child.material.name);
      }
    }
  });
  return names;
}

export function disposeMesh(obj: Mesh<any, any, any>) {
  obj.geometry.dispose();
  if (obj.material.length) {
    for (let i = 0; i < obj.material.length; ++i) {
      if (obj.material[i].map) {
        obj.material[i].map.dispose();
      }

      if (obj.material[i].lightMap) {
        obj.material[i].lightMap.dispose();
      }

      if (obj.material[i].bumpMap) {
        obj.material[i].bumpMap.dispose();
      }

      if (obj.material[i].normalMap) {
        obj.material[i].normalMap.dispose();
      }

      if (obj.material[i].specularMap) {
        obj.material[i].specularMap.dispose();
      }

      if (obj.material[i].envMap) {
        obj.material[i].envMap.dispose();
      }

      obj.material[i].dispose();
    }
  } else {
    if (obj.material.map) {
      obj.material.map.dispose();
    }

    if (obj.material.lightMap) {
      obj.material.lightMap.dispose();
    }

    if (obj.material.bumpMap) {
      obj.material.bumpMap.dispose();
    }

    if (obj.material.normalMap) {
      obj.material.normalMap.dispose();
    }

    if (obj.material.specularMap) {
      obj.material.specularMap.dispose();
    }

    if (obj.material.envMap) {
      obj.material.envMap.dispose();
    }

    obj.material.dispose();
  }

  obj.removeFromParent();

  return true;
}

export function attributeMapToAttributePairs(attributeMap: AttributeMap, attributePairs: AttributePair[] = []) {
  for (const [attribute, attributeSetting] of attributeMap) {
    if (attributeSetting?.attributeValue)
      attributePairs.push({ attribute: attribute, attributeValue: attributeSetting?.attributeValue });
    if (attributeSetting && attributeSetting.map) attributeMapToAttributePairs(attributeSetting.map, attributePairs);
  }
  return attributePairs;
}

export function recursivelyBuildDefaultChildrenMap(attributes: Attribute[]): AttributeMap | undefined {
  if (attributes.length === 0) {
    return undefined;
  }

  const attributeMap = new Map<Attribute, AttributeSetting | undefined>();

  for (const attribute of attributes) {
    let defaultValue = attribute.defaultValue;
    if (defaultValue === null || defaultValue === undefined) {
      defaultValue = attribute.attributeValues[0];
    }
    let isApplied = true;
    const attrType = getAttributeValuePairType(attribute, defaultValue);
    if (attrType && ATTRIBUTES_THAT_ARENT_IMMIDIATELY_APPLIED.includes(attrType)) isApplied = false;
    if (defaultValue instanceof AttributeValue) {
      attributeMap.set(attribute, {
        isApplied: isApplied,
        attributeValue: defaultValue,
        map: recursivelyBuildDefaultChildrenMap(defaultValue.attributes),
      });
    } else {
      attributeMap.set(attribute, {
        isApplied: isApplied,
        attributeValue: defaultValue,
        map: undefined,
      });
    }
  }

  return attributeMap;
}

export function recursiveAttributeSettingsDTOToAttributemap(
  attributeSettingsDTO: AttributeSettingsDTO[],
  attributes: Attribute[],
) {
  const attributeMap = new Map<Attribute, AttributeSetting | undefined>();

  for (const attribute of attributes) {
    for (const attributeSettingDTO of attributeSettingsDTO) {
      if (attribute.id === attributeSettingDTO.key?.toString()) {
        if (attributeSettingDTO.numericValue) {
          attributeMap.set(attribute, {
            attributeValue: attributeSettingDTO.numericValue,
            map: undefined,
            priority: attributeSettingDTO.priority,
          });
        } else {
          for (const attributeValue of attribute.attributeValues) {
            if (attributeValue.id === attributeSettingDTO.value?.toString()) {
              /* if(attributeSettingDTO.numericValue){
                attributeMap.set(attribute, {
                  atributeValue: attributeValue,
                  map: undefined
                });
              } */
              attributeMap.set(attribute, {
                attributeValue: attributeValue,
                map: recursiveAttributeSettingsDTOToAttributemap(attributeSettingsDTO, attributeValue.attributes),
                priority: attributeSettingDTO.priority,
              });
            }
          }
        }
      }
    }
  }

  return attributeMap;
}

export function findProductFromObject(object: Object3D): Product | undefined {
  while (!(object instanceof Product) && object.parent) {
    object = object.parent;
  }

  if (object instanceof Product) return object;
  return undefined;
}

export function findWallFromObject(object: Object3D): Wall {
  while (!(object instanceof Wall) && object.parent) {
    object = object.parent;
  }

  return object as Wall;
}
export function clippingVectorFromBox3(bbox: Box3) {
  const rotatedVector = new Vector3(
    (bbox.max.x - bbox.min.x) / 2,
    (bbox.max.y - bbox.min.y) / 2,
    (bbox.max.z - bbox.min.z) / 2,
  );

  // Return the projected Vector2 (ignoring the Y component)
  // Adjust this based on which plane you want to project onto
  return new Vector2(Math.abs(rotatedVector.x), Math.abs(rotatedVector.z));
}

export function absoluteVector(vector: Vector3) {
  const clonedVector = vector.clone();
  clonedVector.setX(Math.abs(vector.x));
  clonedVector.setY(Math.abs(vector.y));
  clonedVector.setZ(Math.abs(vector.z));
  return clonedVector;
}

// Universal function to check if a string is a valid enum value
export function isValidEnumValue<T extends { [key: string]: string }>(enumObj: T, value: string): boolean {
  return Object.values(enumObj).includes(value);
}

export function getProductsGlobalAttributes(product: Product) {
  const globalAttributes: GlobalAttributeData[] = [];
  recursivelySearchProductsForGlobalAttributes(product, globalAttributes);
  return globalAttributes;
}

function recursivelySearchProductsForGlobalAttributes(product: Product, globalAttributes: GlobalAttributeData[]) {
  for (const attribute of product.productBlueprint.attributes) {
    recursivelySearchAttributesForGlobalAttributes(product, attribute, globalAttributes);
  }
  for (const childProduct of product.children) {
    if (childProduct instanceof Product) {
      recursivelySearchProductsForGlobalAttributes(childProduct, globalAttributes);
    }
  }
}

function recursivelySearchAttributesForGlobalAttributes(
  product: Product,
  attribute: Attribute,
  globalAttributes: GlobalAttributeData[],
) {
  if (attribute.isGlobal) {
    globalAttributes.push({
      product: product,
      attribute: attribute,
      attributeSetting: getProductsAttributeSettingFromAttribute(product, attribute),
    });
  } else {
    for (const attributeValue of attribute.attributeValues) {
      for (const attributeOfAttributeValue of attributeValue.attributes) {
        recursivelySearchAttributesForGlobalAttributes(product, attributeOfAttributeValue, globalAttributes);
      }
    }
  }
}

function getProductsAttributeSettingFromAttribute(product: Product, attribute: Attribute) {
  const attributeMap = product.attributeSettings.generateAttributeMap();
  return recursivelySearchAttributeMapFromAttribute(attributeMap, attribute);
}

function recursivelySearchAttributeMapFromAttribute(
  attributeMap: AttributeMap,
  attribute: Attribute,
): AttributeMap | undefined {
  for (const [attributeOfAttributeMap, attributeSetting] of attributeMap) {
    if (attributeOfAttributeMap === attribute) return attributeMap;
    if (attributeSetting && attributeSetting.map) {
      const res = recursivelySearchAttributeMapFromAttribute(attributeSetting.map, attribute);
      if (res) {
        return res;
      }
    }
  }
  return undefined;
}

export function getCenteredSelfBoundingBoxFromProductRecursively(product: Product) {
  const proudctCollisionDescriptors = product.getCollisionDescriptors(CollisionDescriptorUsageType.SELF);
  let boundingBox;
  if (proudctCollisionDescriptors && proudctCollisionDescriptors.length > 0) {
    /*       for (const productCollisionDesciptor of proudctCollisionDescriptors) {
      if (productCollisionDesciptor instanceof OBBCollisionDescriptor) {
        const OBBhelper = productCollisionDesciptor.getOBB().clone();
        OBBhelper.center.copy(product.position);
        _createOBBHelper(OBBhelper, 0xff0000, this._scene);
      }
    } */
    boundingBox = colisionDescriptorsToCenteredBoundingBox(proudctCollisionDescriptors);
    /* boundingBox.min.add(product.position);
    boundingBox.max.add(product.position); */
  } else {
    boundingBox = product.centeredBoundingBox.clone();

    /*  _createBBHelper(boundingBox, 0xff0000, this._scene); */
  }
  /* boundingBox.applyMatrix4(product.matrixWorld); */

  for (const child of product.children) {
    if (child instanceof Product && child.visible) {
      boundingBox.union(getCenteredSelfBoundingBoxFromProductRecursively(child).applyMatrix4(child.matrix));
    }
  }

  return boundingBox;
}

export function getSelfBoundingBoxFromProductRecursively(product: Product) {
  const proudctCollisionDescriptors = product.getCollisionDescriptors(CollisionDescriptorUsageType.SELF);
  let boundingBox;
  if (proudctCollisionDescriptors && proudctCollisionDescriptors.length > 0) {
    /*       for (const productCollisionDesciptor of proudctCollisionDescriptors) {
      if (productCollisionDesciptor instanceof OBBCollisionDescriptor) {
        const OBBhelper = productCollisionDesciptor.getOBB().clone();
        OBBhelper.center.copy(product.position);
        _createOBBHelper(OBBhelper, 0xff0000, this._scene);
      }
    } */
    boundingBox = colisionDescriptorsToBoundingBox(proudctCollisionDescriptors);
    /* boundingBox.min.add(product.position);
    boundingBox.max.add(product.position); */
  } else {
    boundingBox = product.centeredBoundingBox.clone();

    /*  _createBBHelper(boundingBox, 0xff0000, this._scene); */
  }
  boundingBox.applyMatrix4(product.matrixWorld);

  for (const child of product.children) {
    if (child instanceof Product && child.visibility !== ProductVisibilityENUM.Invisible) {
      boundingBox.union(getSelfBoundingBoxFromProductRecursively(child));
    }
  }

  return boundingBox;
}

export function getLocalBoundingBoxFromDescriptor(
  product: Product,
  type: CollisionDescriptorUsageType,
  calculateChildren = true,
) {
  const proudctCollisionDescriptors = product.getCollisionDescriptors(type);
  let boundingBox;

  if (proudctCollisionDescriptors && proudctCollisionDescriptors.length > 0) {
    boundingBox = colisionDescriptorsToBoundingBox(proudctCollisionDescriptors);
  } else {
    boundingBox = product.centeredBoundingBox;
  }

  if (calculateChildren) {
    for (const child of product.children) {
      if (child instanceof Product && child.visibility !== ProductVisibilityENUM.Invisible) {
        const childBBox = getLocalBoundingBoxFromDescriptor(child, type);
        childBBox.min.add(child.position);
        childBBox.max.add(child.position);

        boundingBox.union(childBBox);
      }
    }
  }

  return boundingBox;
}

export function getNeighbourWall(surfaces: Wall[], currentWall: Wall, offset: number): Wall | null {
  const productWallIndex = surfaces.findIndex((element: Wall) => element === currentWall);

  if (productWallIndex === -1) {
    return null;
  }

  const numWalls = surfaces.length - 2; // need to remove last two bacause its added flooring and ceiling at the end.
  const newWallIndex = (productWallIndex + offset + numWalls) % numWalls;

  return surfaces[newWallIndex];
}

export function checkIfObjectOrItsParentsIsInvisible(object: Object3D) {
  if (!object.visible) return true;
  if (object.parent === null) {
    return false;
  }
  return checkIfObjectOrItsParentsIsInvisible(object.parent);
}

export function findMeshByMeshName(meshName: string, object: Object3D): Mesh[] {
  // Iterate through the children of the group
  let returnMesh: Mesh[] = [];
  for (let i = 0; i < object.children.length; i++) {
    const child = object.children[i];
    const childMesh = child as Mesh;
    // Check if the child is a mesh and has a material
    if (childMesh.isMesh && childMesh.name === meshName) {
      returnMesh = [...returnMesh, childMesh];
    }

    // If the child is not a mesh, recursively search its children
    if (child.children.length > 0) {
      const mesh = findMeshByMeshName(meshName, childMesh);
      if (mesh) {
        returnMesh = [...returnMesh, ...mesh];
      }
    }
  }

  // If no texture with the specified name was found, return undefined
  return returnMesh;
}

export function findBlendaAttributeFromProductBlueprint(
  productBlueprint: ProductBlueprint,
  attributes: Attribute[],
  side: EdgeDirectionEnum,
  hasblenda = true,
): Attribute | undefined {
  for (const attribute of attributes) {
    for (const attributeValue of attribute.attributeValues) {
      if (
        attribute.builderData &&
        attribute.builderData[EDGE_DIRECTION_STRING] === side &&
        attributeValue.builderData &&
        attributeValue.builderData[BLENDA_STRING] === hasblenda
      )
        return attribute;
      const found = findBlendaAttributeFromProductBlueprint(productBlueprint, attributeValue.attributes, side);
      if (found) return found;
    }
  }
  return undefined;
}

export function findNoBlendaFromBlueprint(
  productBlueprint: ProductBlueprint,
  attributes: Attribute[],
  direction: EdgeDirectionEnum,
): AttributePair | undefined {
  for (const attribute of attributes) {
    for (const attributeValue of attribute.attributeValues) {
      if (
        attribute.builderData &&
        attribute.builderData[EDGE_DIRECTION_STRING] &&
        attribute.builderData[EDGE_DIRECTION_STRING] === direction &&
        (!attributeValue.builderData ||
          (!attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING] &&
            !attributeValue.builderData[IS_EDGE_ATTRIBUTE_STRING] &&
            !attributeValue.builderData[BLENDA_STRING]))
      ) {
        return { attribute: attribute, attributeValue: attributeValue };
      }
      const foundFromChildAttributes = findNoBlendaFromBlueprint(
        productBlueprint,
        attributeValue.attributes,
        direction,
      );
      if (foundFromChildAttributes) return foundFromChildAttributes;
    }
  }
  return undefined;
}

export function findProductRelationshipFromProductBlueprint(
  productBlueprint: ProductBlueprint,
  productRelationshipId: string,
) {
  for (const attribute of productBlueprint.attributes) {
    const found = findProductRelationshipFromAttribute(attribute, productRelationshipId);
    if (found) return found;
  }
  for (const productRelationshipGroup of productBlueprint.productRelationshipGroups) {
    for (const productRelationship of productRelationshipGroup.productRelationships) {
      if (productRelationship.id === productRelationshipId) return productRelationship;
    }
  }
  return undefined;
}

export function findProductRelationshipFromAttribute(
  attribute: Attribute,
  productRelationshipId: string,
): { productRelationship: ProductRelationship; attribute: Attribute } | undefined {
  for (const attributeValue of attribute.attributeValues) {
    if (attributeValue instanceof ModelValue) {
      const found = attributeValue.productRelationships.find(
        (productRelationship) => productRelationship.id === productRelationshipId,
      );
      if (found) return { productRelationship: found, attribute: attribute };
    }
    for (const attributeOfAttributeValue of attributeValue.attributes) {
      const found = findProductRelationshipFromAttribute(attributeOfAttributeValue, productRelationshipId);
      if (found) return found;
    }
  }
  return undefined;
}

export function getProductDTOsDefaultAttributeSettings(productBlueprintDTO: ProductBlueprintDTO) {
  const attributeSettingsDTO: AttributeSettingsDTO[] = [];

  getProductDTOsDefaultAttributeSettingsRecursively(attributeSettingsDTO, productBlueprintDTO.attributes);
  return attributeSettingsDTO;
}

export function getProductDTOsDefaultAttributeSettingsRecursively(
  attributeSettingsDTO: AttributeSettingsDTO[],
  attributeDTOs: AttributeDTO[],
) {
  for (const attributeDTO of attributeDTOs) {
    if (attributeDTO.type === AttributeTypeENUM.NumericChange) {
      attributeSettingsDTO.push(new AttributeSettingsDTO(attributeDTO.id, undefined, attributeDTO.defaultValue));
    } else {
      attributeSettingsDTO.push(new AttributeSettingsDTO(attributeDTO.id, attributeDTO.defaultValue));
    }
    for (const attributeValueDTO of attributeDTO.attributeValues) {
      getProductDTOsDefaultAttributeSettingsRecursively(attributeSettingsDTO, attributeValueDTO.attributes);
    }
  }
}

export function findSelectedAttributeValueFromAttributeSettings(
  attributeDTO: AttributeDTO,
  attributeSettingsDTO: AttributeSettingsDTO[],
) {
  for (const attributeSettingDTO of attributeSettingsDTO) {
    if (attributeSettingDTO.key === attributeDTO.id) {
      if (attributeSettingDTO.numericValue !== undefined) {
        return attributeSettingDTO.numericValue;
      }
      for (const attributeValueDTO of attributeDTO.attributeValues) {
        if (attributeValueDTO.id === attributeSettingDTO.value) {
          return attributeValueDTO;
        }
      }
    }
  }

  return undefined;
}

export function setProductDTOAttributeValue(
  productDTO: ProductDTO,
  attributeDTO: AttributeDTO,
  attributeValueDTO: AttributeValueDTO | number,
) {
  const foundAttributeSetting = productDTO.attributeSettings.find(
    (attributeSetting) => attributeSetting.key === attributeDTO.id,
  );
  if (foundAttributeSetting !== undefined) {
    if (typeof attributeValueDTO === 'number') {
      foundAttributeSetting.numericValue = attributeValueDTO;
    } else {
      foundAttributeSetting.value = attributeValueDTO.id;
    }
    return true;
  }
  return false;
}

export function findAttributeByExternalIdRecursively(
  attributes: AttributeDTO[],
  externalId: string,
): AttributeDTO | undefined {
  for (const attribute of attributes) {
    if (attribute.externalId === externalId) return attribute;
    for (const attributeValue of attribute.attributeValues) {
      const found = findAttributeByExternalIdRecursively(attributeValue.attributes, externalId);
      if (found !== undefined) {
        return found;
      }
    }
  }
  return undefined;
}

export function findAttributeValueByExternalIdRecursively(
  attributes: AttributeDTO[],
  externalId: string,
): AttributeValueDTO | undefined {
  for (const attribute of attributes) {
    for (const attributeValue of attribute.attributeValues) {
      if (attributeValue.externalId === externalId) return attributeValue;
      const found = findAttributeValueByExternalIdRecursively(attributeValue.attributes, externalId);
      if (found !== undefined) {
        return found;
      }
    }
  }
  return undefined;
}

export function tryToApplyReferenceAttributeValueToProductDTOs(
  attributeDTO: AttributeDTO,
  attributeValueDTO: AttributeValueDTO,
  products: ProductDTO[],
  availibleProductBlueprints: ProductBlueprintDTO[],
) {
  if (
    attributeDTO.attributeIds === undefined ||
    attributeDTO.attributeIds.length === 0 ||
    attributeValueDTO.attributeValueIds === undefined ||
    attributeValueDTO.attributeValueIds.length === 0
  ) {
    return;
  }
  for (const product of products) {
    tryToApplyReferenceAttributeValueToProductDTOsRecursively(
      attributeDTO,
      attributeValueDTO,
      product,
      availibleProductBlueprints,
    );
  }
}

export function tryToApplyReferenceAttributeValueToProductDTOsRecursively(
  attributeDTO: AttributeDTO,
  attributeValueDTO: AttributeValueDTO,
  product: ProductDTO,
  availibleProductBlueprints: ProductBlueprintDTO[],
) {
  const foundProductBlueprint = availibleProductBlueprints.find(
    (productBlueprint) => productBlueprint.id === product.productBlueprintId,
  );
  if (foundProductBlueprint !== undefined) {
    for (const productsAttributeDTO of foundProductBlueprint.attributes) {
      tryToApplyReferenceAttributeValueToProductDTOsRecursivelyByAttribute(
        attributeDTO,
        attributeValueDTO,
        product,
        productsAttributeDTO,
      );
    }
  }
  for (const child of product.products) {
    tryToApplyReferenceAttributeValueToProductDTOsRecursively(
      attributeDTO,
      attributeValueDTO,
      child.childProduct,
      availibleProductBlueprints,
    );
  }
}

export function getAttributeValueFromProductDTOAndAttributeDTO(attributeDTO: AttributeDTO, productDTO: ProductDTO) {
  const attSetting = productDTO.attributeSettings.find((attSetting) => {
    return attSetting.key === attributeDTO.id;
  });
  if (attSetting?.value !== undefined) return attSetting.value;
  if (attSetting?.numericValue !== undefined) return attSetting.numericValue;
  return undefined;
}

export function changeNumericAttirbuteOfProductDTO(attribute: AttributeDTO, value: number, product: ProductDTO) {
  const attributeSetting = product.attributeSettings.find((attributeSettingToSearch) => {
    return attributeSettingToSearch.key === attribute.id;
  });
  if (attributeSetting) attributeSetting.numericValue = value;
}

export function tryToApplyReferenceAttributeValueToProductDTOsRecursivelyByAttribute(
  attributeDTO: AttributeDTO,
  attributeValueDTO: AttributeValueDTO,
  product: ProductDTO,
  attributeDTOOfProduct: AttributeDTO,
) {
  for (const attributesAttributeValue of attributeDTOOfProduct.attributeValues) {
    for (const referencedAttributeId of attributeDTO.attributeIds!) {
      if (attributeDTOOfProduct.id === referencedAttributeId) {
        for (const referencedAttributeValueId of attributeValueDTO.attributeValueIds) {
          if (referencedAttributeValueId === attributesAttributeValue.id) {
            setProductDTOAttributeValue(product, attributeDTOOfProduct, attributesAttributeValue);
          }
        }
      }
    }
    for (const attributeValuesAttribute of attributesAttributeValue.attributes) {
      tryToApplyReferenceAttributeValueToProductDTOsRecursivelyByAttribute(
        attributeDTO,
        attributeValueDTO,
        product,
        attributeValuesAttribute,
      );
    }
  }
}

export function findAttributeFromProductBlueprint(attributeId: string, productBlueprint: ProductBlueprint) {
  return recursivelyFindAttributeFromProductBlueprint(attributeId, productBlueprint.attributes);
}

export function recursivelyFindAttributeFromProductBlueprint(
  attributeId: string,
  attributes: Attribute[],
): Attribute | undefined {
  for (const attribute of attributes) {
    if (attribute.id === attributeId) return attribute;

    for (const attributeValue of attribute.attributeValues) {
      const found = recursivelyFindAttributeFromProductBlueprint(attributeId, attributeValue.attributes);
      if (found) return found;
    }
  }
  return undefined;
}

export function findAttributeValueFomProductBlueprint(attributeValueId: string, productBluepint: ProductBlueprint) {
  return recursivelyFindAttributeValueFomProductBlueprint(attributeValueId, productBluepint.attributes);
}
export function recursivelyFindAttributeValueFomProductBlueprint(
  attributeValueId: string,
  attributes: Attribute[],
): AttributeValue | undefined {
  for (const attribute of attributes) {
    for (const attributeValue of attribute.attributeValues) {
      if (attributeValue.id === attributeValueId) return attributeValue;
      const found = recursivelyFindAttributeValueFomProductBlueprint(attributeValueId, attributeValue.attributes);
      if (found) return found;
    }
  }
  return undefined;
}

export function getProductBlueprintsFlattenedAttributes(productBluepint: ProductBlueprint) {
  const flattenedAttributesArray: Attribute[] = [];
  getProductBlueprintsFlattenedAttributesRecursively(productBluepint.attributes, flattenedAttributesArray);
  return flattenedAttributesArray;
}
export function getProductBlueprintsFlattenedAttributesRecursively(
  attributes: Attribute[],
  flattenedAttributesArray: Attribute[],
) {
  for (const attribute of attributes) {
    flattenedAttributesArray.push(attribute);
    for (const attributeValue of attribute.attributeValues) {
      getProductBlueprintsFlattenedAttributesRecursively(attributeValue.attributes, flattenedAttributesArray);
    }
  }
}

export function flattenSteps(steps: StepDTO[]): StepDTO[] {
  const result: StepDTO[] = [];

  const helper = (items: StepDTO[]) => {
    for (const item of items) {
      result.push({ ...item, steps: [] }); // drop nested steps
      if (item.steps && item.steps.length > 0) {
        helper(item.steps);
      }
    }
  };

  helper(steps);
  return result;
}

export function materialNeedsTangents(mesh: Mesh) {
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return mats.some((m: any) => m?.normalMap && m.normalMapType === TangentSpaceNormalMap);
}
